import React, { useMemo, useState } from 'react';
import {
  U21Table,
  U21LoadError,
  U21Section,
} from 'app/shared/u21-ui/components';
import { AlertHitResponse } from 'app/modules/alerts/types/responses';
import { useFetchAlertHitEntities } from 'app/modules/alerts/queries/useFetchAlertHitEntities';
import { useSelector } from 'react-redux';
import { selectEntityTableConfig } from 'app/shared/CustomConfig/selectors';
import {
  createPaginationPayload,
  createTableColumnConfig,
} from 'app/shared/utils/table';
import { ENTITY_COLUMN_CONFIG } from 'app/modules/entities/columns';
import { ShortEntityResponse } from 'app/modules/entities/responses';
import styled from 'styled-components';
import { PaginationPayload } from 'app/shared/pagination/models';

const DEFAULT_PAGINATION: PaginationPayload = {
  limit: 5,
  offset: 1,
  sort_column: 'id',
  sort_direction: 'descending',
};

interface AlertUpdateEntityProps {
  hit: AlertHitResponse;
}

export const AlertUpdateEntity: React.FC<AlertUpdateEntityProps> = ({
  hit,
}) => {
  const entityTableConfig = useSelector(selectEntityTableConfig);
  const [pagination, setPagination] = useState<PaginationPayload>({
    ...DEFAULT_PAGINATION,
    limit: 0, // disable the hook's API call until after onStateChange -> double API call
  });
  const columns = useMemo(
    () =>
      createTableColumnConfig<ShortEntityResponse>(
        entityTableConfig,
        ENTITY_COLUMN_CONFIG,
      ),
    [entityTableConfig],
  );

  const { data, isLoading, isError, refetch } = useFetchAlertHitEntities(
    hit.id,
    pagination,
  );

  let content: React.ReactNode;
  if (isError) {
    content = <U21LoadError label="entities" onTryAgain={refetch} />;
  } else {
    content = (
      <U21Table<ShortEntityResponse>
        label="entity"
        columns={columns}
        data={data?.entities || []}
        count={data?.count ?? 0}
        manualPagination
        defaultPageSize={5}
        onStateChange={(state) => setPagination(createPaginationPayload(state))}
        loading={isLoading}
      />
    );
  }

  return (
    <BorderlessSection title="Entities Added" collapsible>
      {content}
    </BorderlessSection>
  );
};

const BorderlessSection = styled(U21Section)`
  border: none;
`;
